import {
  CartesianGrid,
  Scatter,
  ScatterChart as RechartsScatterChart,
  Tooltip,
  XAxis,
  YAxis,
  ZAxis,
} from 'recharts';
import { ChartFrame } from '../../molecules/ChartFrame/ChartFrame';
import type { CartesianChartProps, ChartRow } from '../../molecules/ChartFrame/chartProps';
import { chartAriaLabel, isEmpty } from '../../molecules/ChartFrame/chartProps';
import { AnchoredTooltipSurface } from '../../molecules/ChartFrame/AnchoredTooltipSurface';
import { useAnchoredTooltip } from '../../molecules/ChartFrame/useAnchoredTooltip';
import {
  chartAnimation,
  chartMarkGeometry,
  gridProps,
  legendItems,
  seriesColor,
  tooltipCursor,
  xAxisProps,
  yAxisProps,
} from '../../molecules/ChartFrame/chartTheme';
import { ChartLegend } from '../../molecules/ChartLegend/ChartLegend';
import { ChartTooltip, type ChartTooltipEntry } from '../../molecules/ChartTooltip/ChartTooltip';

/**
 * A scatter chart: discrete points on a shared category axis.
 *
 * The shape to reach for when the readings are **events rather than a series**
 * — a handful of moments scattered across a date axis, where a line would
 * invent a path between them and a bar would claim every bucket had one. A
 * viral post is the example this was built for: it happened on a Tuesday, it
 * did not "trend up" into the Tuesday.
 *
 * It takes the same `data` / `xKey` / `series` triple every other chart here
 * takes, so it consumes an `aggregate()` result directly and a caller can swap
 * it for `LineChart` without reshaping anything. Internally each series becomes
 * its own `<Scatter>` over the **full row spine** with a `null` where that
 * series has no point — Recharts skips a point with no y coordinate, so a
 * sparse series draws sparsely while every series still shares one category
 * domain. Handing each `<Scatter>` a pre-filtered subset instead would give
 * Recharts as many category domains as there are series, and they would not
 * line up.
 *
 * **Not in Figma.** The file draws the cartesian chrome and stops — there is no
 * scatter, no bubble and no point size anywhere in it. The axes, gridlines,
 * legend and tooltip are the file's; the marks come from `chartMarkGeometry`,
 * where each value says what it was derived from. See plan.md §7 item 132.
 *
 * ```tsx
 * <ScatterChart data={rows} xKey="x" series={series} sizeBy="value" />
 * ```
 */
/*
 * `formatDetail` is omitted rather than accepted and dropped: it hands its
 * formatter the hovered *row*, and a scatter's tooltip is built from a
 * `ScatterPoint` — one x/y pair lifted out of a row that may have held several
 * series. There is no single row to pass, so the prop has no honest meaning
 * here.
 */
export type ScatterChartProps = Omit<CartesianChartProps, 'formatDetail'> & {
  /**
   * What a point's size means.
   *
   * `none` draws every point the same, which is right when the y position is
   * the whole reading. `value` scales the area by the point's own y value — a
   * redundant encoding on purpose: it is what makes the one big moment in a
   * field of small ones visible before the reader has looked at the axis.
   */
  sizeBy?: 'none' | 'value';
};

/**
 * Recharts' scatter tooltip payload is one entry per *axis* — x, y and z — so
 * the default readout would print the category, the value and the value again
 * under three axis names. The point already knows which series it belongs to,
 * so the payload is rebuilt from it: one row, the series' own label and colour.
 */
type ScatterPoint = {
  y: number | null;
  /** The source row and series key, so `formatCompare` can reach the prior period. */
  row: ChartRow;
  seriesKey: string;
  z: number;
  seriesLabel: string;
  seriesColor: string;
};

/**
 * The readout for one category — every series that actually plotted a point
 * there.
 *
 * It used to report the single mark under the pointer, because that is all
 * Recharts reported. Anchoring made the category the unit instead: the readout
 * is pinned to the band, so it has to say what the band holds. The same "whole
 * point, not per series" rule the drill-down follows.
 *
 * **Blank series are dropped**, unlike the cartesian payload, which lists every
 * series at every point. A scatter is sparse by construction — most series have
 * no mark at most categories — so listing the absent ones would bury the one or
 * two that are there under a column of dashes.
 */
function scatterTooltipPayload(
  series: readonly { color: string; data: readonly ScatterPoint[] }[],
  index: number,
): ChartTooltipEntry[] {
  return series
    .map((s) => s.data[index])
    .filter((point): point is ScatterPoint => point !== undefined && point.y !== null)
    .map((point) => ({
      name: point.seriesLabel,
      value: point.y ?? undefined,
      color: point.seriesColor,
      dataKey: point.seriesKey,
      payload: point.row,
    }));
}

export function ScatterChart({
  data,
  xKey,
  series,
  height,
  yDomain,
  yTicks,
  yAxisWidth,
  formatAxisValue,
  legend = 'bottom',
  tooltip = true,
  loading = false,
  empty,
  emptyLabel,
  formatValue,
  formatCompare,
  xAxisTitle,
  yAxisTitle,
  label,
  sizeBy = 'none',
  onViewDetails,
  viewDetailsLabel,
  className,
}: ScatterChartProps) {
  const items = legendItems(series);

  /*
   * One point per row per series, `y: null` where the series has nothing at
   * that category. The colour is resolved here rather than in the tooltip so
   * the swatch in the readout and the mark under the cursor cannot disagree.
   */
  const points = series.map((s, i) => {
    const color = seriesColor(i, s.color);
    return {
      series: s,
      color,
      data: data.map((row: ChartRow) => {
        const raw = row[s.key];
        const y = raw === null || raw === undefined || raw === '' ? null : Number(raw);
        return {
          [xKey]: row[xKey],
          y,
          row,
          seriesKey: s.key,
          /*
           * `z` drives the size. On `sizeBy: 'none'` every point carries the
           * same figure, so the `ZAxis` range collapses to one radius.
           */
          z: sizeBy === 'value' ? (y ?? 0) : chartMarkGeometry.scatterPointArea,
          seriesLabel: s.label,
          seriesColor: color,
        };
      }),
    };
  });

  const anchor = useAnchoredTooltip({
    count: data.length,
    enabled: tooltip,
    onDrill: onViewDetails,
  });
  const anchoredEntries =
    anchor.point === null ? [] : scatterTooltipPayload(points, anchor.point.index);

  return (
    <ChartFrame
      height={height}
      xAxisTitle={xAxisTitle}
      yAxisTitle={yAxisTitle}
      loading={loading}
      empty={empty ?? isEmpty(data, series)}
      emptyLabel={emptyLabel}
      label={label ?? chartAriaLabel('Scatter chart', series, xKey)}
      legend={legend === 'none' ? undefined : <ChartLegend items={items} />}
      legendPlacement="bottom"
      plotRef={anchor.plotRef}
      plotProps={anchor.plotProps}
      hover={
        anchoredEntries.length > 0 && (
          <AnchoredTooltipSurface anchor={anchor}>
            <ChartTooltip
              active
              hideLabel
              payload={anchoredEntries}
              formatter={formatValue && ((v) => formatValue(Number(v)))}
              compare={
                formatCompare &&
                ((entry) =>
                  entry.payload ? formatCompare(entry.payload, String(entry.dataKey)) : undefined)
              }
              action={anchor.drill}
              actionLabel={viewDetailsLabel}
              locked={anchor.locked}
            />
          </AnchoredTooltipSurface>
        )
      }
      className={className}
    >
      <RechartsScatterChart {...anchor.chartProps}>
        <CartesianGrid {...gridProps()} />
        {/*
         * `allowDuplicatedCategory={false}` because every series carries the
         * same spine: without it Recharts concatenates the children's
         * categories and draws the axis once per series.
         */}
        <XAxis {...xAxisProps(xKey, data.length)} type="category" allowDuplicatedCategory={false} />
        <YAxis
          {...yAxisProps(yAxisWidth)}
          type="number"
          dataKey="y"
          {...(yDomain ? { domain: yDomain, allowDataOverflow: false as const } : {})}
          {...(yTicks ? { ticks: yTicks, interval: 0 as const } : {})}
          tickFormatter={formatAxisValue ?? formatValue}
        />
        {/*
         * `ZAxis` never renders; it only maps `z` onto a point's area. A single
         * `range` value pins every point to one size, which is what `none`
         * wants — two equal bounds would make Recharts divide by a zero span.
         */}
        <ZAxis
          type="number"
          dataKey="z"
          range={
            sizeBy === 'value'
              ? chartMarkGeometry.scatterSizeRange
              : [chartMarkGeometry.scatterPointArea, chartMarkGeometry.scatterPointArea]
          }
        />

        {tooltip && (
          /* Cursor only — see `LineChart`, where this arrangement is explained. */
          <Tooltip
            {...anchor.cursorProps}
            isAnimationActive={false}
            content={() => null}
            cursor={tooltipCursor('line')}
          />
        )}

        {points.map(({ series: s, color, data: seriesData }) => (
          <Scatter
            key={s.key}
            name={s.label}
            data={seriesData}
            fill={color}
            fillOpacity={chartMarkGeometry.scatterFillOpacity}
            {...chartAnimation}
          />
        ))}
      </RechartsScatterChart>
    </ChartFrame>
  );
}
